import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Default branding values
const defaultBranding = {
  name: 'MyMedMember',
  logo_url: null,
  primary_color: '#14b8a6',
  secondary_color: '#0d9488'
};

// Create context
const BrandingContext = createContext({
  branding: defaultBranding,
  loading: true,
  error: null,
  refetch: () => {}
});

// Provider component
export const BrandingProvider = ({ children }) => {
  const [branding, setBranding] = useState(defaultBranding);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchBranding = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/clinic/branding`);
      const data = response.data;
      
      setBranding({
        name: data.name || defaultBranding.name,
        logo_url: data.logo_url || null,
        primary_color: data.primary_color || defaultBranding.primary_color,
        secondary_color: data.secondary_color || defaultBranding.secondary_color
      });
      
      // Apply colors to CSS variables
      applyBrandingColors(
        data.primary_color || defaultBranding.primary_color,
        data.secondary_color || defaultBranding.secondary_color
      );
      
      setError(null);
    } catch (err) {
      console.error('Failed to fetch branding:', err);
      setError(err.message);
      // Apply default colors on error
      applyBrandingColors(defaultBranding.primary_color, defaultBranding.secondary_color);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Check if user is authenticated before fetching
    const token = localStorage.getItem('token');
    if (token) {
      fetchBranding();
    } else {
      setLoading(false);
      applyBrandingColors(defaultBranding.primary_color, defaultBranding.secondary_color);
    }

    // Listen for branding updates
    const handleBrandingUpdate = () => {
      fetchBranding();
    };
    window.addEventListener('clinic-branding-updated', handleBrandingUpdate);

    return () => {
      window.removeEventListener('clinic-branding-updated', handleBrandingUpdate);
    };
  }, []);

  return (
    <BrandingContext.Provider value={{ branding, loading, error, refetch: fetchBranding }}>
      {children}
    </BrandingContext.Provider>
  );
};

// Hook to use branding
export const useBranding = () => {
  const context = useContext(BrandingContext);
  if (!context) {
    throw new Error('useBranding must be used within a BrandingProvider');
  }
  return context;
};

// Helper function to apply branding colors to CSS variables
export const applyBrandingColors = (primaryColor, secondaryColor) => {
  const root = document.documentElement;
  
  // Convert hex to RGB for opacity variants
  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : { r: 20, g: 184, b: 166 }; // Default teal
  };

  const primaryRgb = hexToRgb(primaryColor);
  const secondaryRgb = hexToRgb(secondaryColor);

  // Set CSS custom properties
  root.style.setProperty('--brand-primary', primaryColor);
  root.style.setProperty('--brand-secondary', secondaryColor);
  root.style.setProperty('--brand-primary-rgb', `${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}`);
  root.style.setProperty('--brand-secondary-rgb', `${secondaryRgb.r}, ${secondaryRgb.g}, ${secondaryRgb.b}`);
  
  // Light variants (for backgrounds)
  root.style.setProperty('--brand-primary-light', `rgba(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}, 0.1)`);
  root.style.setProperty('--brand-secondary-light', `rgba(${secondaryRgb.r}, ${secondaryRgb.g}, ${secondaryRgb.b}, 0.1)`);
  
  // Hover variants (darker)
  root.style.setProperty('--brand-primary-hover', secondaryColor);
};

// Utility function to get inline style object with branding colors
export const getBrandingStyles = (branding) => ({
  primary: branding?.primary_color || defaultBranding.primary_color,
  secondary: branding?.secondary_color || defaultBranding.secondary_color,
  primaryBg: `${branding?.primary_color || defaultBranding.primary_color}15`,
  gradient: `linear-gradient(135deg, ${branding?.primary_color || defaultBranding.primary_color} 0%, ${branding?.secondary_color || defaultBranding.secondary_color} 100%)`
});

export default BrandingContext;
